import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";

// Public lightbox for a service's photo gallery (opened from the homepage card).
export const ServiceGalleryDialog = ({ service, onClose, onViewProjects }) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => { setIdx(0); }, [service]);
  if (!service) return null;
  const photos = service.photos || [];
  const Icon = service.icon;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="service-gallery-dialog">
        <div className="flex items-center gap-3">
          {Icon && (
            <div className="bg-primary/10 p-2 rounded-lg">
              <Icon className="h-5 w-5 text-primary" />
            </div>
          )}
          <DialogTitle className="text-xl sm:text-2xl font-semibold font-heading" data-testid="service-gallery-title">{service.title}</DialogTitle>
        </div>

        {photos.length > 0 && (
          <div>
            <div className="relative rounded-lg overflow-hidden bg-muted h-[45vh] sm:h-[55vh] flex items-center justify-center">
              <img
                src={photos[idx]}
                alt={`${service.title} — photo ${idx + 1}`}
                className="max-w-full max-h-full object-contain"
                data-testid="service-gallery-main-photo"
              />
              {photos.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() => setIdx((idx - 1 + photos.length) % photos.length)}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
                    aria-label="Previous photo"
                    data-testid="service-gallery-prev"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setIdx((idx + 1) % photos.length)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
                    aria-label="Next photo"
                    data-testid="service-gallery-next"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                  <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full">
                    {idx + 1} / {photos.length}
                  </div>
                </>
              )}
            </div>
            {photos.length > 1 && (
              <div className="flex gap-2 mt-2 overflow-x-auto pb-1">
                {photos.map((p, i) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setIdx(i)}
                    className={`w-16 h-16 rounded overflow-hidden flex-shrink-0 border-2 transition-opacity ${i === idx ? "border-primary" : "border-transparent opacity-70 hover:opacity-100"}`}
                    data-testid={`service-gallery-thumb-${i}`}
                  >
                    <img src={p} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {service.description && (
          <div
            className="text-muted-foreground text-sm sm:text-base prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: service.description }}
          />
        )}

        <Button
          variant="outline"
          className="w-full sm:w-auto"
          onClick={onViewProjects}
          data-testid="service-gallery-view-projects"
        >
          See related projects <ArrowRight className="h-4 w-4 ml-1" />
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default ServiceGalleryDialog;
